import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Globe, GraduationCap, Euro, Calendar, CheckCircle2, ArrowRight,
  FileText, Plane, Building2, Users, Award, BookOpen, Clock
} from 'lucide-react';

const INTERNATIONAL_IMAGE = "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80";

const parcours = [
  {
    year: "1",
    title: "Année préparatoire",
    subtitle: "Formation initiale",
    price: "5 990 €",
    duration: "12 mois",
    mode: "Formation classique",
    features: [
      "Cours 100% présentiel à Saint-Denis",
      "Accompagnement personnalisé",
      "Préparation aux techniques commerciales",
      "Renforcement en français professionnel",
      "Aide à la recherche d'entreprise",
      "Certificat de fin de 1ère année"
    ],
    highlight: "Idéal pour s'intégrer et préparer l'alternance"
  },
  {
    year: "2",
    title: "Année en alternance",
    subtitle: "Contrat d'apprentissage",
    price: "0 €",
    duration: "12 mois",
    mode: "Alternance 1j CFA / 4j entreprise",
    features: [
      "Formation 100% gratuite (OPCO)",
      "Rémunération de 775€ à 1 867€/mois",
      "Diplôme TP NTC - RNCP39063",
      "Expérience professionnelle en France",
      "Accompagnement pour le visa travail",
      "Accès à l'emploi en France"
    ],
    highlight: "Formation rémunérée avec diplôme reconnu"
  }
];

const eligibility = [
  {
    icon: Globe,
    title: "Nationalité",
    desc: "Ressortissant hors Union Européenne"
  },
  {
    icon: GraduationCap,
    title: "Niveau d'études",
    desc: "Baccalauréat ou équivalent"
  },
  {
    icon: FileText,
    title: "Niveau de français",
    desc: "B1 minimum (B2 recommandé)"
  },
  {
    icon: Calendar,
    title: "Âge",
    desc: "18-29 ans"
  }
];

const steps = [
  { num: "01", title: "Candidature en ligne", desc: "Remplissez le formulaire et joignez vos documents" },
  { num: "02", title: "Test de français", desc: "Évaluation de votre niveau (entretien visio)" },
  { num: "03", title: "Admission", desc: "Réponse sous 15 jours" },
  { num: "04", title: "Visa étudiant", desc: "Accompagnement pour vos démarches Campus France" },
  { num: "05", title: "Rentrée", desc: "Intégration en septembre" }
];

const faq = [
  {
    q: "Comment se passe le paiement ?",
    a: "Une première tranche de 1 500€ est demandée à l'inscription. Elle est remboursable à 90% (soit 1 350€) en cas de refus de visa ou de non-admission. Le solde de 4 490€ peut être réglé en 3, 6 ou 10 fois sans frais après validation de votre dossier."
  },
  {
    q: "Pourquoi une 1ère année payante ?",
    a: "La 1ère année vous permet d'obtenir un visa étudiant, de vous adapter au système français et de trouver une entreprise pour l'alternance. Sans contrat d'apprentissage, la formation ne peut pas être financée par l'OPCO."
  },
  {
    q: "Puis-je travailler pendant la 1ère année ?",
    a: "Oui, avec un visa étudiant vous pouvez travailler jusqu'à 964 heures par an (environ 20h/semaine) pour financer vos études."
  },
  {
    q: "L'école m'aide-t-elle à trouver une entreprise ?",
    a: "Oui, nous vous accompagnons activement dans votre recherche d'entreprise dès le 2ème semestre de la 1ère année. Notre réseau de partenaires facilite votre placement."
  },
  {
    q: "Que se passe-t-il si je ne trouve pas d'entreprise ?",
    a: "Nous mettons tout en œuvre pour vous aider. En dernier recours, vous pouvez poursuivre en formation initiale (payante) la 2ème année, ou nous vous accompagnons dans une réorientation."
  },
  {
    q: "Le diplôme est-il le même ?",
    a: "Oui, le diplôme obtenu est identique : TP Négociateur Technico-Commercial (RNCP39063), reconnu par l'État, équivalent Bac+2."
  }
];

export const International = () => {
  return (
    <div data-testid="international-page">
      {/* Hero Section */}
      <section className="bg-[#0B1F3A] py-20" data-testid="international-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Globe className="text-[#C8A75A]" size={24} />
                <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A]">
                  Étudiants internationaux
                </span>
              </div>
              <h1 className="font-serif-heading text-4xl sm:text-5xl font-semibold text-white mb-6">
                Votre parcours vers l'emploi en France
              </h1>
              <div className="w-16 h-0.5 bg-[#C8A75A] mb-6"></div>
              <p className="text-[#F5F5F5]/90 text-lg mb-8 leading-relaxed">
                Un parcours en 2 ans spécialement conçu pour les étudiants étrangers : 
                une année préparatoire puis une année en alternance rémunérée avec 
                un diplôme reconnu et un accès direct à l'emploi en France.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="bg-white/10 px-4 py-2 text-white text-sm">
                  <span className="text-[#C8A75A] font-semibold">2 ans</span> de formation
                </div>
                <div className="bg-white/10 px-4 py-2 text-white text-sm">
                  <span className="text-[#C8A75A] font-semibold">Bac+2</span> reconnu
                </div>
                <div className="bg-white/10 px-4 py-2 text-white text-sm">
                  <span className="text-[#C8A75A] font-semibold">Visa</span> accompagné
                </div>
              </div>

              <Link to="/etudiants#candidature">
                <Button className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-8 py-6" data-testid="international-cta">
                  Candidater maintenant
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <img 
                src={INTERNATIONAL_IMAGE}
                alt="Étudiants internationaux"
                className="w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#C8A75A] p-6 text-[#0B1F3A]">
                <Plane className="mb-2" size={28} />
                <div className="font-semibold">Visa étudiant</div>
                <div className="text-sm">Accompagnement inclus</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parcours en 2 ans */}
      <section className="py-20 bg-white" data-testid="parcours-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Votre parcours
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A]">
              2 années pour réussir
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {parcours.map((year, index) => (
              <div 
                key={index}
                className={`border-2 ${index === 0 ? 'border-[#0B1F3A]' : 'border-[#C8A75A]'} overflow-hidden`}
                data-testid={`parcours-year-${year.year}`}
              >
                {/* Header */}
                <div className={`p-6 ${index === 0 ? 'bg-[#0B1F3A] text-white' : 'bg-[#C8A75A] text-[#0B1F3A]'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className={`text-4xl font-serif-heading font-bold ${index === 0 ? 'text-[#C8A75A]' : 'text-[#0B1F3A]'}`}>
                        {year.year}
                      </span>
                      <div>
                        <h3 className="font-semibold text-lg">{year.title}</h3>
                        <p className={`text-sm ${index === 0 ? 'text-[#F5F5F5]/80' : 'text-[#0B1F3A]/70'}`}>
                          {year.subtitle}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold">{year.price}</div>
                      <div className={`text-xs ${index === 0 ? 'text-[#F5F5F5]/70' : 'text-[#0B1F3A]/60'}`}>
                        {index === 0 ? 'Frais de scolarité' : 'Financé OPCO'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <Clock size={14} /> {year.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <Building2 size={14} /> {year.mode}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <ul className="space-y-3 mb-6">
                    {year.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-[#4A4A4A]">
                        <CheckCircle2 className="text-[#C8A75A] flex-shrink-0 mt-0.5" size={18} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className={`p-3 text-center text-sm font-medium ${index === 0 ? 'bg-[#F5F5F5] text-[#0B1F3A]' : 'bg-[#C8A75A]/10 text-[#C8A75A]'}`}>
                    {year.highlight}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Arrow between */}
          <div className="hidden md:flex justify-center my-8">
            <div className="flex items-center gap-4 text-[#C8A75A]">
              <div className="h-0.5 w-32 bg-[#C8A75A]"></div>
              <ArrowRight size={24} />
              <div className="h-0.5 w-32 bg-[#C8A75A]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions d'éligibilité */}
      <section className="py-20 bg-[#F5F5F5]" data-testid="eligibility-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Éligibilité
            </span>
            <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A]">
              Conditions d'admission
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {eligibility.map((item, index) => (
              <div key={index} className="bg-white p-6 border border-[#E6E6E6] text-center">
                <div className="w-14 h-14 bg-[#0B1F3A] flex items-center justify-center mx-auto mb-4">
                  <item.icon className="text-[#C8A75A]" size={28} />
                </div>
                <h3 className="font-semibold text-[#0B1F3A] mb-2">{item.title}</h3>
                <p className="text-[#4A4A4A] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Étapes d'admission */}
      <section className="py-20 bg-[#0B1F3A]" data-testid="steps-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Processus
            </span>
            <h2 className="font-serif-heading text-3xl font-semibold text-white">
              Étapes d'admission
            </h2>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <div key={index} className="text-center" data-testid={`step-${index}`}>
                <div className="w-16 h-16 bg-[#C8A75A] flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#0B1F3A] font-serif-heading text-xl font-bold">
                    {step.num}
                  </span>
                </div>
                <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                <p className="text-[#F5F5F5]/70 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financement */}
      <section className="py-20 bg-white" data-testid="financing-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
                Financement
              </span>
              <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A] mb-6">
                Comment financer votre 1ère année ?
              </h2>
              <div className="w-16 h-0.5 bg-[#C8A75A] mb-6"></div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-[#F5F5F5]">
                  <Euro className="text-[#C8A75A] flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#0B1F3A]">Première tranche : 1 500 €</h3>
                    <p className="text-[#4A4A4A] text-sm">
                      À l'inscription. <strong>Remboursable à 90%</strong> en cas de refus de visa 
                      ou de non-admission après étude du dossier.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#F5F5F5]">
                  <Euro className="text-[#C8A75A] flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#0B1F3A]">Paiement échelonné</h3>
                    <p className="text-[#4A4A4A] text-sm">Solde en 3, 6 ou 10 fois sans frais après validation</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#F5F5F5]">
                  <Building2 className="text-[#C8A75A] flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#0B1F3A]">Job étudiant</h3>
                    <p className="text-[#4A4A4A] text-sm">Jusqu'à 964h/an de travail autorisé avec visa étudiant</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#F5F5F5]">
                  <Award className="text-[#C8A75A] flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#0B1F3A]">Bourses</h3>
                    <p className="text-[#4A4A4A] text-sm">Renseignez-vous auprès de votre pays d'origine</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#0B1F3A] p-8 text-white">
              <h3 className="font-serif-heading text-2xl font-semibold mb-6 text-[#C8A75A]">
                Récapitulatif des coûts
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-white/20">
                  <div>
                    <div>1ère tranche à l'inscription</div>
                    <div className="text-xs text-[#F5F5F5]/60">Remboursable à 90% si refus</div>
                  </div>
                  <span className="font-bold text-[#C8A75A]">1 500 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/20">
                  <span>Solde 1ère année</span>
                  <span className="font-bold text-[#C8A75A]">4 490 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/20">
                  <span className="font-semibold">Total 1ère année</span>
                  <span className="font-bold text-[#C8A75A]">5 990 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/20">
                  <span>2ème année (alternance)</span>
                  <span className="font-bold text-[#C8A75A]">0 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/20">
                  <span>Rémunération 2ème année</span>
                  <span className="font-bold text-green-400">+9 300 € à +22 400 €</span>
                </div>
                <div className="pt-4">
                  <p className="text-sm text-[#F5F5F5]/70">
                    En 2ème année, vous êtes rémunéré entre 775€ et 1 867€/mois 
                    selon votre âge, soit un gain net sur les 2 ans.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#F5F5F5]" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              FAQ
            </span>
            <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A]">
              Questions fréquentes
            </h2>
          </div>

          <div className="space-y-4">
            {faq.map((item, index) => (
              <div key={index} className="bg-white border border-[#E6E6E6] p-6">
                <h3 className="font-semibold text-[#0B1F3A] mb-2">{item.q}</h3>
                <p className="text-[#4A4A4A] text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-[#0B1F3A]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Globe className="mx-auto text-[#C8A75A] mb-6" size={48} />
          <h2 className="font-serif-heading text-3xl font-semibold text-white mb-4">
            Prêt à commencer votre aventure en France ?
          </h2>
          <p className="text-[#F5F5F5]/80 mb-8 max-w-2xl mx-auto">
            Déposez votre candidature dès maintenant. Notre équipe vous accompagnera 
            dans toutes vos démarches administratives et de visa.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/etudiants#candidature">
              <Button className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-8 py-6 text-base font-semibold">
                Déposer ma candidature
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#0B1F3A] rounded-none px-8 py-6 text-base">
                Poser une question
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default International;
